import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useRouteAuth } from "@/hooks/useRouteAuth";
import Layout from "@/components/Layout";
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import Account from "./pages/Account";
import ProductList from "./pages/ProductList";
import ProductDetail from "./pages/ProductDetail";
import NotFound from "./pages/NotFound";
import MentionsLegales from "./pages/legal/MentionsLegales";
import PolitiqueConfidentialite from "./pages/legal/PolitiqueConfidentialite";
import ConditionsGarantie from "./pages/legal/ConditionsGarantie";
import CGV from "./pages/legal/CGV";
import Location from "./pages/Location";
import DevenirRevendeur from "./pages/DevenirRevendeur";
import ContratMaintenance from "./pages/ContratMaintenance";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import DynamicPage from "./pages/DynamicPage";
import WarrantyVerification from "./pages/WarrantyVerification";

const queryClient = new QueryClient();

const CanonicalUpdater = () => {
  const location = useLocation();
  useEffect(() => {
    const PREFERRED_ORIGIN = "https://next.moncopieur.ma";
    const currentOrigin = window.location.origin;
    const usePreferred = currentOrigin.includes("lovable.app") || currentOrigin.includes("localhost") || currentOrigin.includes("127.0.0.1");
    const base = usePreferred ? PREFERRED_ORIGIN : currentOrigin;
    const canonicalUrl = `${base}${location.pathname}`;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);
  }, [location.pathname]);
  return null;
};

const AppContent = () => {
  useRouteAuth();
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/location" element={<Location />} />
        <Route path="/devenir-revendeur" element={<DevenirRevendeur />} />
        <Route path="/contrat-de-maintenance" element={<ContratMaintenance />} />
        <Route path="/verification-garantie" element={<WarrantyVerification />} />
        <Route path="/account" element={<Account />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
        <Route path="/produits" element={<ProductList />} />
        <Route path="/produit/:slug" element={<ProductDetail />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/conditions-de-garantie" element={<ConditionsGarantie />} />
        <Route path="/cgv" element={<CGV />} />
        {/* Dynamic pages route - must be before catch-all */}
        <Route path="/cwp-politique-de-confidentialite" element={<DynamicPage />} />
        <Route path="/cwp-conditions-generales-de-vente" element={<DynamicPage />} />
        <Route path="/cwp-mentions-legales" element={<DynamicPage />} />
        <Route path="/cwp-conditions-de-garantie" element={<DynamicPage />} />
        <Route path="/page/:slug" element={<DynamicPage />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <SidebarProvider defaultOpen={false}>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <CanonicalUpdater />
            <AppContent />
          </BrowserRouter>
        </SidebarProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
